import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Shield, AlertTriangle, Bug, Search, Filter, ChevronRight, 
  BookOpen, Code, Target, Zap, Database, Lock, Globe,
  FileWarning, AlertCircle, Info, ChevronDown, ChevronUp, ExternalLink
} from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';

const RulesDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState([]);
  const [filteredRules, setFilteredRules] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [selectedRule, setSelectedRule] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, []);

  const filterRules = useCallback(() => {
    let filtered = [...rules];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(rule => 
        rule.name?.toLowerCase().includes(term) ||
        rule.description?.toLowerCase().includes(term) ||
        rule.rule_id?.toLowerCase().includes(term)
      );
    }
    
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(rule => rule.category === categoryFilter);
    }
    
    if (severityFilter !== 'all') {
      filtered = filtered.filter(rule => 
        rule.impact?.severity?.toLowerCase() === severityFilter.toLowerCase()
      );
    }
    
    setFilteredRules(filtered);
  }, [rules, searchTerm, categoryFilter, severityFilter]);

  useEffect(() => {
    filterRules();
  }, [filterRules]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, rulesRes] = await Promise.all([
        api.get('/litesabre-pro/security-rules-stats'),
        api.get('/litesabre-pro/security-rules')
      ]);
      setStats(statsRes.data);
      setRules(rulesRes.data.rules || []);
    } catch (error) {
      toast.error('Failed to load security rules');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'bg-red-600 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-black';
      case 'low': return 'bg-blue-500 text-white';
      default: return 'bg-gray-400 text-white';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Injection': return <Bug className="w-4 h-4" />;
      case 'Access Control': return <Lock className="w-4 h-4" />;
      case 'Security Misconfiguration': return <FileWarning className="w-4 h-4" />;
      case 'Authentication': return <Shield className="w-4 h-4" />;
      case 'Components with Known Vulnerabilities': return <Database className="w-4 h-4" />;
      case 'API Security': return <Globe className="w-4 h-4" />;
      case 'Business Logic': return <Target className="w-4 h-4" />;
      case 'Information Disclosure': return <AlertCircle className="w-4 h-4" />;
      default: return <Code className="w-4 h-4" />;
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getUniqueCategories = () => {
    return [...new Set(rules.map(r => r.category))].filter(Boolean);
  };

  const getRulesByCategory = () => {
    const grouped = {};
    filteredRules.forEach(rule => {
      const cat = rule.category || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(rule);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const ruleStats = stats?.rule_statistics || {};
  const payloadStats = stats?.payload_statistics || {};

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm">Total Rules</p>
                <p className="text-3xl font-bold">{ruleStats.total_rules || 0}</p>
              </div>
              <Shield className="w-10 h-10 text-indigo-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Critical Rules</p>
                <p className="text-3xl font-bold">{ruleStats.by_severity?.critical || 0}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Attack Payloads</p>
                <p className="text-3xl font-bold">{payloadStats.total_attack_payloads || 0}</p>
              </div>
              <Zap className="w-10 h-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Categories</p>
                <p className="text-3xl font-bold">{Object.keys(ruleStats.by_category || {}).length}</p>
              </div>
              <BookOpen className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Rules by Category
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(ruleStats.by_category || {}).map(([category, count]) => (
              <div key={category} className="p-3 bg-gray-50 rounded-lg flex items-center gap-3">
                {getCategoryIcon(category)}
                <div>
                  <p className="text-sm font-medium">{category}</p>
                  <p className="text-xs text-gray-500">{count} rules</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* OWASP Coverage */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            OWASP Coverage
          </CardTitle>
          <CardDescription>Rules mapped to OWASP Top 10 (2021)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(ruleStats.by_owasp || {}).sort().map(([owasp, count]) => (
              <div key={owasp} className="p-3 bg-gray-50 rounded-lg text-center">
                <p className="text-sm font-bold text-indigo-600">{owasp}</p>
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-xs text-gray-500">rules</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Payload Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Attack Payload Library
          </CardTitle>
          <CardDescription>Comprehensive payloads for vulnerability testing</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(payloadStats.breakdown || {}).map(([type, count]) => (
              <div key={type} className="p-3 border rounded-lg">
                <p className="text-sm font-medium capitalize">{type.replace(/_/g, ' ')}</p>
                <p className="text-2xl font-bold text-indigo-600">{count}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Rules Browser */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="w-5 h-5" />
            Security Rules Browser
          </CardTitle>
          <CardDescription>Browse and explore all {rules.length} security rules</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search rules..."
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {getUniqueCategories().map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Rules List by Category */}
          <ScrollArea className="h-[500px]">
            {Object.entries(getRulesByCategory()).map(([category, categoryRules]) => (
              <div key={category} className="mb-4">
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {getCategoryIcon(category)}
                    <span className="font-medium">{category}</span>
                    <Badge variant="outline">{categoryRules.length}</Badge>
                  </div>
                  {expandedCategories[category] ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                
                {expandedCategories[category] && (
                  <div className="mt-2 space-y-2 ml-4">
                    {categoryRules.map(rule => (
                      <div
                        key={rule.rule_id}
                        className="p-3 border rounded-lg hover:border-indigo-300 cursor-pointer transition-colors"
                        onClick={() => setSelectedRule(rule)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <code className="text-xs bg-gray-100 px-2 py-1 rounded">{rule.rule_id}</code>
                              <Badge className={getSeverityColor(rule.impact?.severity)}>
                                {rule.impact?.severity || 'unknown'}
                              </Badge>
                            </div>
                            <h4 className="font-medium mt-1">{rule.name}</h4>
                            <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                              {rule.description}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">
                                {rule.owasp?.id || 'N/A'}
                              </Badge>
                              {rule.cwe?.slice(0, 2).map(cwe => (
                                <Badge key={cwe} variant="outline" className="text-xs">{cwe}</Badge>
                              ))}
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Compliance Frameworks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Compliance Framework Mapping
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {(stats?.compliance_frameworks || []).map(framework => (
              <Badge key={framework} variant="outline" className="text-sm py-1 px-3">
                {framework}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Rule Detail Dialog */}
      <Dialog open={!!selectedRule} onOpenChange={() => setSelectedRule(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedRule && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">{selectedRule.rule_id}</code>
                  <Badge className={getSeverityColor(selectedRule.impact?.severity)}>
                    {selectedRule.impact?.severity}
                  </Badge>
                </DialogTitle>
                <DialogDescription>{selectedRule.name}</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Description */}
                <div>
                  <h4 className="font-medium mb-2">Description</h4>
                  <p className="text-sm text-gray-600">{selectedRule.description}</p>
                </div>

                {/* Classification */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2">OWASP</h4>
                    <Badge variant="outline">
                      {selectedRule.owasp?.id} - {selectedRule.owasp?.name}
                    </Badge>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">CWE</h4>
                    <div className="flex flex-wrap gap-1">
                      {selectedRule.cwe?.map(cwe => (
                        <Badge key={cwe} variant="outline">{cwe}</Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Impact */}
                <div>
                  <h4 className="font-medium mb-2">Impact</h4>
                  <div className="text-sm space-y-1">
                    <p><strong>CVSS Vector:</strong> <code className="text-xs">{selectedRule.impact?.cvss_vector}</code></p>
                    <p><strong>Exploitability:</strong> {selectedRule.impact?.exploitability}</p>
                    <p><strong>Likelihood:</strong> {selectedRule.impact?.likelihood}</p>
                    <p className="text-gray-600">{selectedRule.impact?.impact_description}</p>
                  </div>
                </div>

                {/* Remediation */}
                <div>
                  <h4 className="font-medium mb-2">Remediation</h4>
                  <p className="text-sm text-green-700 bg-green-50 p-3 rounded-lg">
                    {selectedRule.remediation?.short_description}
                  </p>
                  {selectedRule.remediation?.long_description && (
                    <p className="text-sm text-gray-600 mt-2">
                      {selectedRule.remediation?.long_description}
                    </p>
                  )}
                </div>

                {/* Compliance Mapping */}
                {selectedRule.mapped_controls && (
                  <div>
                    <h4 className="font-medium mb-2">Compliance Mapping</h4>
                    <div className="space-y-2 text-sm">
                      {selectedRule.mapped_controls.uspcf?.length > 0 && (
                        <p><strong>USPCF:</strong> {selectedRule.mapped_controls.uspcf.join(', ')}</p>
                      )}
                      {selectedRule.mapped_controls.iso_27001?.length > 0 && (
                        <p><strong>ISO 27001:</strong> {selectedRule.mapped_controls.iso_27001.join(', ')}</p>
                      )}
                      {selectedRule.mapped_controls.nist_800_53?.length > 0 && (
                        <p><strong>NIST 800-53:</strong> {selectedRule.mapped_controls.nist_800_53.join(', ')}</p>
                      )}
                      {selectedRule.mapped_controls.pci_dss?.length > 0 && (
                        <p><strong>PCI-DSS:</strong> {selectedRule.mapped_controls.pci_dss.join(', ')}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Scanner Config */}
                {selectedRule.scanner?.payloads?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Sample Payloads</h4>
                    <div className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs font-mono max-h-[150px] overflow-y-auto">
                      {selectedRule.scanner.payloads.slice(0, 5).map((p, i) => (
                        <div key={i} className="mb-1">{p.value || p.id}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* References */}
                {selectedRule.remediation?.references?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">References</h4>
                    <div className="space-y-1">
                      {selectedRule.remediation.references.map((ref, i) => (
                        <a
                          key={i}
                          href={ref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-indigo-600 hover:underline flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {ref}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RulesDashboard;
